import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { formatDate } from '../utils/formatHelpers';
import {
  InsightsPills,
  ShipmentsChart,
  CustomersChart,
  ItemsChart,
  ItemsTable,
  ItemsTotals,
  SeasonalHeatmap,
} from '../components/analytics';
import { exportAnalyticsTab, seasonalRowsFromHeatmap } from '../utils/analyticsExport';
import '../styles/Reports.css';

const MONTH_LABELS = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];

const VIEW_TABS = [
  { id: 'shipments', label: 'Shipments' },
  { id: 'customers', label: 'Customers' },
  { id: 'items',     label: 'Items'     },
  { id: 'seasons',   label: 'Seasons'   },
];

/* ─── Date range calculator ────────────────────────────────────────── */
function toDateStr(d) { return d.toISOString().split('T')[0]; }

function calcDateRange(preset, customFrom, customTo) {
  const today = new Date();
  switch (preset) {
    case 'month':
      return {
        from: toDateStr(new Date(today.getFullYear(), today.getMonth(), 1)),
        to: toDateStr(today),
      };
    case 'fiscal':
      return { from: `${today.getFullYear()}-01-01`, to: `${today.getFullYear()}-12-31` };
    case 'last12': {
      const past = new Date(today);
      past.setFullYear(past.getFullYear() - 1);
      return { from: toDateStr(past), to: toDateStr(today) };
    }
    case 'custom':
      return { from: customFrom || toDateStr(today), to: customTo || toDateStr(today) };
    default:
      return { from: `${today.getFullYear()}-01-01`, to: toDateStr(today) };
  }
}

/* ═══════════════════════════════════════════════════════════════════ */
const Analytics = () => {
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (!isAdmin()) {
      window.location.href = '/dashboard';
    }
  }, [isAdmin]);

  const [preset,     setPreset]     = useState('fiscal');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [activeTab,  setActiveTab]  = useState('shipments');
  const [customerId, setCustomerId] = useState('all');
  const [customers,  setCustomers]  = useState([]);
  const [basis,      setBasis]      = useState('shipped');

  const dateRange = useMemo(
    () => calcDateRange(preset, customFrom, customTo),
    [preset, customFrom, customTo]
  );

  /* per-tab chart data */
  const [shipmentsData, setShipmentsData] = useState(null);
  const [customersData, setCustomersData] = useState(null);
  const [itemsData,     setItemsData]     = useState(null);
  const [itemsTotals,   setItemsTotals]   = useState(null);
  const [customersTotals, setCustomersTotals] = useState(null);
  const [seasonalData,  setSeasonalData]  = useState(null);
  const [insights,      setInsights]      = useState(null);

  const [chartLoading,   setChartLoading]   = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);

  /* drill-down */
  const [drill, setDrill] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  /* ── fetch customers list once ──────────────────────────────────── */
  useEffect(() => {
    api.get('/customers')
      .then(r => setCustomers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCustomers([]));
  }, []);

  /* ── build API params ───────────────────────────────────────────── */
  const apiParams = useMemo(() => {
    const p = { dateFrom: dateRange.from, dateTo: dateRange.to, basis };
    if (customerId !== 'all') p.customerId = customerId;
    return p;
  }, [dateRange.from, dateRange.to, customerId, basis]);

  /* ── fetch insights ─────────────────────────────────────────────── */
  useEffect(() => {
    setInsightLoading(true);
    api.get('/analytics/insights', { params: apiParams })
      .then(r => {
        const d = r.data || {};
        setInsights({
          busiest_month: d.busiestMonth
            ? { value: d.busiestMonth.count, label: d.busiestMonth.name, month: d.busiestMonth.month }
            : null,
          top_customer: d.topCustomer
            ? { value: d.topCustomer.count, label: d.topCustomer.name }
            : null,
          top_item: d.topItem
            ? { value: Math.round(Number(d.topItem.count)), label: d.topItem.name }
            : null,
          peak_season: d.peakSeason
            ? { value: '—', label: d.peakSeason }
            : null,
        });
      })
      .catch(() => setInsights(null))
      .finally(() => setInsightLoading(false));
  }, [apiParams]);

  /* ── fetch chart data ───────────────────────────────────────────── */
  useEffect(() => {
    setDrill(null);
    setChartLoading(true);

    const fetchers = {
      shipments: () =>
        api.get('/analytics/shipments-by-month', { params: apiParams })
           .then(r => {
             const cur = (r.data?.data || []).map(d => ({ ...d, year: 'current' }));
             const prev = (r.data?.previousData || []).map(d => ({ ...d, year: 'prev' }));
             setShipmentsData([...cur, ...prev]);
           }),
      customers: () =>
        api.get('/analytics/top-customers', { params: apiParams })
           .then(r => {
             setCustomersData((r.data?.data || []).map(c => ({
               customer_id: c.id, customer_name: c.name,
               shipment_count: c.shipment_count,
               total_cases: c.total_cases, total_units: c.total_units,
               total_weight: c.total_weight,
             })));
             setCustomersTotals(r.data?.totals || null);
           }),
      items: () =>
        api.get('/analytics/top-items', { params: apiParams })
           .then(r => {
             setItemsData((r.data?.data || []).map(i => ({
               item_id: i.id, item_name: i.name, part_number: i.part_number,
               total_cases: i.total_cases, total_units: i.total_units,
               total_weight: i.total_weight, pallet_count: i.pallet_count,
             })));
             setItemsTotals(r.data?.totals || null);
           }),
      seasons: () =>
        api.get('/analytics/seasonal', { params: { years: 4, customerId: apiParams.customerId, basis: apiParams.basis } })
           .then(r => {
             const yearData = r.data?.data || [];
             const years = yearData.map(d => d.year);
             const cells = {};
             yearData.forEach(yd => {
               yd.months.forEach((count, idx) => { cells[`${yd.year}-${idx + 1}`] = count; });
             });
             setSeasonalData({ years, cells });
           }),
    };

    (fetchers[activeTab] || (() => Promise.resolve()))()
      .catch(() => {})
      .finally(() => setChartLoading(false));
  }, [activeTab, apiParams]);

  /* ── drill-down handlers ────────────────────────────────────────── */
  const handleShipmentBarClick = useCallback(async ({ month, label }) => {
    setDrill({ type: 'shipments', month, label, rows: null });
    setDrillLoading(true);
    try {
      const res = await api.get('/shipments');
      const all = Array.isArray(res.data) ? res.data : [];
      const rows = all
        .filter(s => {
          const d = new Date(s.created_at);
          const ds = toDateStr(d);
          const matchMonth = d.getMonth() + 1 === month;
          const matchDate = ds >= dateRange.from && ds <= dateRange.to;
          const matchCust = customerId === 'all' || String(s.customer_id) === String(customerId);
          return matchMonth && matchDate && matchCust;
        })
        .map(s => ({ ...s, date_label: formatDate(s.created_at) }));
      setDrill(prev => ({ ...prev, rows }));
    } catch {
      setDrill(prev => ({ ...prev, rows: [] }));
    } finally {
      setDrillLoading(false);
    }
  }, [dateRange, customerId]);

  const handleCustomerBarClick = useCallback(async (row) => {
    setDrill({ type: 'customers', customer_id: row.customer_id, customer_name: row.customer_name, rows: null });
    setDrillLoading(true);
    try {
      const res = await api.get('/shipments');
      const all = Array.isArray(res.data) ? res.data : [];
      const rows = all
        .filter(s => {
          const ds = toDateStr(new Date(s.created_at));
          return String(s.customer_id) === String(row.customer_id) &&
                 ds >= dateRange.from && ds <= dateRange.to;
        })
        .map(s => ({ ...s, date_label: formatDate(s.created_at) }));
      setDrill(prev => ({ ...prev, rows }));
    } catch {
      setDrill(prev => ({ ...prev, rows: [] }));
    } finally {
      setDrillLoading(false);
    }
  }, [dateRange]);

  const handleItemClick = useCallback(async (row) => {
    setDrill({ type: 'items', item_id: row.item_id, item_name: row.item_name, rows: null });
    setDrillLoading(true);
    try {
      const res = await api.get('/inventory/items');
      const all = Array.isArray(res.data) ? res.data : [];
      const rows = all.filter(i => String(i.item_id) === String(row.item_id));
      setDrill(prev => ({ ...prev, rows }));
    } catch {
      setDrill(prev => ({ ...prev, rows: [] }));
    } finally {
      setDrillLoading(false);
    }
  }, []);

  const closeDrill = useCallback(() => setDrill(null), []);

  /* ── insight stat click → switch to relevant tab ──────────────────── */
  const handleStatClick = useCallback((key) => {
    const tabMap = {
      busiest_month: 'shipments',
      top_customer: 'customers',
      top_item: 'items',
      peak_season: 'seasons',
    };
    if (tabMap[key]) setActiveTab(tabMap[key]);
  }, []);

  /* ── export the active tab ──────────────────────────────────────── */
  /* The Shipments chart holds current and prior year as one flat array; the
     export needs one row per month with both years side by side. */
  const shipmentsExportRows = useMemo(() => {
    if (!shipmentsData) return [];
    const byMonth = new Map();
    shipmentsData.forEach(d => {
      const m = Number(d.month);
      if (!byMonth.has(m)) byMonth.set(m, { month_label: MONTH_LABELS[m - 1] });
      const row = byMonth.get(m);
      if (d.year === 'prev') {
        row.prev_shipment_count = d.count;
        row.prev_total_weight = d.total_weight;
      } else {
        row.shipment_count = d.count;
        row.total_weight = d.total_weight;
      }
    });
    return [...byMonth.entries()].sort((a, b) => a[0] - b[0]).map(([, row]) => row);
  }, [shipmentsData]);

  const sumBy = (rows, key) =>
    rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0);

  const handleExport = useCallback(() => {
    const customerName = customerId === 'all'
      ? null
      : (customers.find(c => String(c.id) === String(customerId))?.name || null);
    const filters = { dateFrom: dateRange.from, dateTo: dateRange.to, customerName, basis };

    if (activeTab === 'items') {
      const rows = itemsData || [];
      return exportAnalyticsTab('items', {
        rows,
        totals: itemsTotals && {
          cases:  itemsTotals.total_cases,
          units:  itemsTotals.total_units,
          weight: itemsTotals.total_weight,
          /* pallets is deliberately blank — a pallet carries several items, so
             summing each item's pallet count would double-count pallets. */
        },
        filters,
      });
    }

    if (activeTab === 'customers') {
      const rows = customersData || [];
      return exportAnalyticsTab('customers', {
        rows,
        totals: {
          shipments: customersTotals?.total_shipments ?? sumBy(rows, 'shipment_count'),
          cases:     customersTotals?.total_cases     ?? sumBy(rows, 'total_cases'),
          units:     customersTotals?.total_units     ?? sumBy(rows, 'total_units'),
          weight:    customersTotals?.total_weight    ?? sumBy(rows, 'total_weight'),
        },
        filters,
      });
    }

    if (activeTab === 'shipments') {
      const rows = shipmentsExportRows;
      return exportAnalyticsTab('shipments', {
        rows,
        totals: {
          shipments:     sumBy(rows, 'shipment_count'),
          weight:        sumBy(rows, 'total_weight'),
          prevShipments: sumBy(rows, 'prev_shipment_count'),
          prevWeight:    sumBy(rows, 'prev_total_weight'),
        },
        filters,
      });
    }

    const rows = seasonalRowsFromHeatmap(seasonalData);
    return exportAnalyticsTab('seasons', {
      rows,
      totals: {
        months: MONTH_LABELS.map((_, i) => sumBy(rows.map(r => ({ v: r.months[i] })), 'v')),
        total:  sumBy(rows, 'total'),
      },
      filters,
    });
  }, [activeTab, itemsData, itemsTotals, customersData, customersTotals,
      shipmentsExportRows, seasonalData, customerId, customers, dateRange, basis]);

  const exportDisabled = chartLoading || (
    activeTab === 'items'     ? !itemsData?.length :
    activeTab === 'customers' ? !customersData?.length :
    activeTab === 'shipments' ? !shipmentsExportRows.length :
                                !seasonalData?.years?.length
  );

  /* ── render ─────────────────────────────────────────────────────── */
  return (
    <div className="analytics-page page-container">
      <div className="page-header">
        <div className="header-top">
          <h1>Analytics</h1>
        </div>
      </div>

      <InsightsPills insights={insights} loading={insightLoading} onStatClick={handleStatClick} />

      {/* Filter bar */}
      <div className="analytics-filter-bar">
        <select
          className="filter-select"
          value={preset}
          onChange={e => setPreset(e.target.value)}
        >
          <option value="month">This Month</option>
          <option value="fiscal">Fiscal Year</option>
          <option value="last12">Last 12 Months</option>
          <option value="custom">Custom Dates</option>
        </select>

        <select
          className="filter-select"
          value={basis}
          onChange={e => setBasis(e.target.value)}
        >
          <option value="shipped">Shipped Only</option>
          <option value="all">All (Including Unshipped)</option>
        </select>

        <select
          className="filter-select"
          value={customerId}
          onChange={e => setCustomerId(e.target.value)}
        >
          <option value="all">All Customers</option>
          {customers.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        {preset === 'custom' && (
          <div className="analytics-custom-dates">
            <input type="date" className="date-input" value={customFrom}
              onChange={e => setCustomFrom(e.target.value)} />
            <span className="date-separator">to</span>
            <input type="date" className="date-input" value={customTo}
              onChange={e => setCustomTo(e.target.value)} />
          </div>
        )}
      </div>

      <div className="analytics-tabs" role="tablist">
        {VIEW_TABS.map(tab => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`analytics-tab ${activeTab === tab.id ? 'analytics-tab--active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}

        {/* The Items tab carries its own export button above its table, beside the
            data it exports; the chart tabs have no table to hang one on. */}
        {activeTab !== 'items' && (
          <button className="export-btn analytics-export-btn"
                  onClick={handleExport} disabled={exportDisabled}>
            Export CSV
          </button>
        )}
      </div>

      <div className="analytics-card">
        {activeTab === 'shipments' && (
          <ShipmentsChart data={shipmentsData} loading={chartLoading}
            drillData={drill?.type === 'shipments' ? drill : null}
            drillLoading={drillLoading} onBarClick={handleShipmentBarClick} onDrillClose={closeDrill} />
        )}
        {activeTab === 'customers' && (
          <CustomersChart data={customersData} loading={chartLoading}
            drillData={drill?.type === 'customers' ? drill : null}
            drillLoading={drillLoading} onBarClick={handleCustomerBarClick} onDrillClose={closeDrill} />
        )}
        {activeTab === 'items' && (
          <>
            <ItemsTotals totals={itemsTotals} loading={chartLoading} />
            <ItemsChart data={itemsData} loading={chartLoading}
              drillData={drill?.type === 'items' ? drill : null}
              drillLoading={drillLoading} onItemClick={handleItemClick} onDrillClose={closeDrill} />
            <ItemsTable rows={itemsData || []} totals={itemsTotals} loading={chartLoading}
              onItemClick={handleItemClick} onExport={handleExport} />
          </>
        )}
        {activeTab === 'seasons' && (
          <SeasonalHeatmap data={seasonalData} loading={chartLoading} />
        )}
      </div>
    </div>
  );
};

export default Analytics;
